import { useMemo, useState, useEffect, useCallback } from 'react';
import { appConfirm } from '../../../../components/AppConfirmModal';

/**
 * Custom hook to manage the state and pagination logic for shared master lists.
 * Mimics useBranchListScreen.js and useDepartmentListScreen.js patterns.
 *
 * @param {Object} config The active master configuration object from registry.
 */
export function useSharedMasterListScreen(config) {
  const [activeTab, setActiveTab] = useState('Active');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isLoading, setIsLoading] = useState(false);
  // Local mutable copy of the config data to support archive/delete without a real API
  const [localData, setLocalData] = useState(() => ({
    Active: config?.data?.Active ? [...config.data.Active] : [],
    Archive: config?.data?.Archive ? [...config.data.Archive] : [],
  }));

  // Reset local data when the config/master changes
  useEffect(() => {
    setLocalData({
      Active: config?.data?.Active ? [...config.data.Active] : [],
      Archive: config?.data?.Archive ? [...config.data.Archive] : [],
    });
  }, [config?.id]);

  // Trigger brief simulation of loading state when switching master configuration
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 150);
    return () => clearTimeout(timer);
  }, [config?.id]);

  // Retrieve base data array based on the selected tab (Active/Archive/All)
  const baseRows = useMemo(() => {
    const mockDates = [
      'Apr 22, 2026',
      'Apr 24, 2026',
      'Jul 07, 2026',
      'Jul 24, 2026',
      'Aug 10, 2026'
    ];

    const active = (localData.Active || []).map(row => ({
      ...row,
      status: 'Active',
      isArchived: false,
      createdDate: row.createdDate || mockDates[row.id % mockDates.length]
    }));

    const archive = (localData.Archive || []).map(row => ({
      ...row,
      status: 'Archive / Deactive',
      isArchived: true,
      createdDate: row.createdDate || mockDates[(row.id + 2) % mockDates.length]
    }));

    if (activeTab === 'Active') return active;
    if (activeTab === 'Archive') return archive;
    return [...active, ...archive];
  }, [localData, activeTab]);

  const [searchTarget, setSearchTarget] = useState('Name');

  // Filter rows based on search text (case-insensitive substring match on Name or Under)
  const filteredRows = useMemo(() => {
    const query = searchText.toLowerCase().trim();
    if (!query) return baseRows;
    if (searchTarget === 'Under') {
      return baseRows.filter(row => row.under && row.under.toLowerCase().includes(query));
    }
    return baseRows.filter(row => (row.name && row.name.toLowerCase().includes(query)) || (row.under && row.under.toLowerCase().includes(query)));
  }, [baseRows, searchText, searchTarget]);

  const totalCount = filteredRows.length;

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / pageSize));
  }, [totalCount, pageSize]);

  // Adjust current page if it overflows the new page bounds after filter changes
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  // Segment rows for current page pagination
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const rangeStart = useMemo(() => {
    if (totalCount === 0) return 0;
    return (page - 1) * pageSize + 1;
  }, [page, pageSize, totalCount]);

  const rangeEnd = useMemo(() => {
    if (totalCount === 0) return 0;
    return Math.min(totalCount, page * pageSize);
  }, [page, pageSize, totalCount]);

  const rangeLabel = useMemo(() => {
    return `Showing ${rangeStart} - ${rangeEnd} of ${totalCount} entries`;
  }, [rangeStart, rangeEnd, totalCount]);

  function setActiveTabAndReset(tab) {
    setActiveTab(tab);
    setPage(1);
  }

  function setSearchTextAndReset(text) {
    setSearchText(text);
    setPage(1);
  }

  function setPageSizeAndReset(size) {
    setPageSize(Number(size));
    setPage(1);
  }

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return;
    setPage(nextPage);
  }

  function goToPreviousPage() {
    setPage(prev => Math.max(1, prev - 1));
  }

  function goToNextPage() {
    if (page < totalPages) {
      setPage(prev => prev + 1);
    }
  }

  const handleArchive = useCallback((id) => {
    setLocalData(prev => {
      const activeItem = prev.Active.find(r => r.id === id);
      const archiveItem = prev.Archive.find(r => r.id === id);
      if (activeItem) {
        // Move from Active → Archive
        return {
          Active: prev.Active.filter(r => r.id !== id),
          Archive: [...prev.Archive, activeItem],
        };
      } else if (archiveItem) {
        // Move from Archive → Active
        return {
          Active: [...prev.Active, archiveItem],
          Archive: prev.Archive.filter(r => r.id !== id),
        };
      }
      return prev;
    });
  }, []);

  const handleDelete = useCallback(async (id) => {
    const confirmed = await appConfirm({
      title: 'Delete Entry',
      message: 'Are you sure you want to permanently delete this entry?',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    setLocalData(prev => ({
      Active: prev.Active.filter(r => r.id !== id),
      Archive: prev.Archive.filter(r => r.id !== id),
    }));
  }, []);

  return {
    state: {
      activeTab,
      searchText,
      searchTarget,
      page,
      pageSize,
      isLoading,
      rows: paginatedRows,
      totalCount,
      totalPages,
      rangeLabel,
      canPrevious: page > 1,
      canNext: page < totalPages,
    },
    actions: {
      setActiveTab: setActiveTabAndReset,
      setSearchText: setSearchTextAndReset,
      setSearchTarget,
      setPageSize: setPageSizeAndReset,
      goToPage,
      goToPreviousPage,
      goToNextPage,
      handleArchive,
      handleDelete,
    }
  };
}
export default useSharedMasterListScreen;
